import {
  type ComponentPropsWithoutRef,
  forwardRef,
  type ReactElement,
  type ReactNode,
} from 'react';
import { type To } from 'react-router';
import cx from 'clsx';
import { Link } from '@repo/poly-hooks/components';
import {
  type ButtonStyleProps,
  getButtonClassNames,
  resolveButtonVariantProps,
} from '@repo/design-system-kit/components/button/button';
import internalLinkStyles from './internal-link.module.css';

export interface InternalLinkProps
  extends Omit<ComponentPropsWithoutRef<'a'>, 'href'>, ButtonStyleProps {
  to: To;
  replace?: boolean;
  label?: string;
  display?: 'link' | 'button';
}

/**
 * Internal navigation link component with optional button styling.
 * Use for navigation within the app (not external URLs).
 *
 * @example
 * // As a regular link
 * <InternalLink to="/login">Sign in</InternalLink>
 *
 * @example
 * // Styled as a button
 * <InternalLink display="button" variant="primary" to="/dashboard">
 *   Go to Dashboard
 * </InternalLink>
 */
export const InternalLink = forwardRef<HTMLAnchorElement, InternalLinkProps>(function InternalLink(
  {
    children,
    label,
    display = 'link',
    className,
    to,
    replace,
    target,
    onClick,
    variant,
    size,
    iconOnly = false,
    stretch = false,
    iconLeft,
    iconRight,
    ...props
  },
  ref
): ReactElement {
  const isButtonDisplay = display === 'button';
  let classes = cx('body-standard-link', internalLinkStyles.link, className);
  let resolvedIconLeft: ReactNode;
  let resolvedIconRight: ReactNode;

  if (isButtonDisplay) {
    const { effectiveVariant, effectiveIconLeft, effectiveIconRight, effectiveSize } =
      resolveButtonVariantProps({ variant, iconOnly, iconLeft, iconRight, size });
    classes = getButtonClassNames({
      variant: effectiveVariant,
      size: effectiveSize,
      iconOnly,
      stretch,
      className,
    });
    resolvedIconLeft = effectiveIconLeft;
    resolvedIconRight = effectiveIconRight;
  }

  return (
    <Link
      ref={ref}
      className={classes}
      replace={replace}
      target={target}
      to={to}
      onClick={onClick}
      {...props}
    >
      {isButtonDisplay ? (
        <>
          {resolvedIconLeft}
          {label ?? children}
          {resolvedIconRight}
        </>
      ) : (
        (label ?? children)
      )}
    </Link>
  );
});
